// tailDetection/tailDetection.js
import { findLocalMaxima, findInflectionPoints } from '../core/peakDetection.js';
import { findIntensityAtTime } from './tailUtils.js';
import { createTailPointNote, calculateTailDurations } from './tailUtils.js';

// Simple tail detection - exclude points with unnatural pitch drops
export const detectTails = (speechSegment, segmentPitchPoints, segmentIntensityPoints, globalMaxIntensity, pitchDerivative) => {
  const tails = [];
  
  if (segmentPitchPoints.length < 3) return tails;
  
  // Find local maxima within this segment
  const localMaxima = findLocalMaxima(segmentPitchPoints, segmentIntensityPoints, globalMaxIntensity);
  
  if (localMaxima.length === 0) return tails;
  
  // Get the last local maxima time
  const lastMaximaTime = localMaxima[localMaxima.length - 1].time;
  
  // Get all points after the last maxima (this is our tail)
  const tailPoints = segmentPitchPoints.filter(p => p.time > lastMaximaTime);
  
  if (tailPoints.length < 2) return tails;
  
  console.log(`Found tail: ${tailPoints.length} points after last maxima at ${lastMaximaTime.toFixed(2)}s`);
  
  // FILTER: Exclude points with unnatural pitch drops (steep negative derivatives)
  const naturalTailPoints = tailPoints.filter((point, index) => {
    if (index === 0) return true; // Always include first point
    
    const intensity = findIntensityAtTime(segmentIntensityPoints, point.time);
    const hasPitch = point.pitch > 50;
    const hasIntensity = intensity > (globalMaxIntensity * 0.1);
    
    // Calculate pitch change from previous point
    const prevPoint = tailPoints[index - 1];
    const timeDiff = point.time - prevPoint.time;
    const pitchDiff = point.pitch - prevPoint.pitch;
    const pitchChangeRate = timeDiff > 0 ? pitchDiff / timeDiff : 0;
    
    // Exclude if pitch drops too sharply (more than 200 Hz/second)
    const isNaturalDrop = pitchChangeRate > -200; // Not too steep
    
    if (!isNaturalDrop) {
      console.log(`Excluded tail point at ${point.time.toFixed(2)}s: steep pitch drop (${pitchChangeRate.toFixed(0)} Hz/s)`);
    }
    
    return hasPitch && hasIntensity && isNaturalDrop;
  });
  
  if (naturalTailPoints.length < 2) {
    console.log(`Tail discarded: only ${naturalTailPoints.length} natural points after filtering steep drops`);
    return tails;
  }
  
  console.log(`Natural tail: ${naturalTailPoints.length} points after filtering steep pitch drops`);
  
  // Find inflection points in the natural tail
  const inflectionPoints = findInflectionPoints(naturalTailPoints);
  
  tails.push({
    segment: speechSegment,
    tailPoints: naturalTailPoints,
    inflectionPoints: inflectionPoints,
    tailDuration: naturalTailPoints[naturalTailPoints.length - 1].time - naturalTailPoints[0].time,
    lastMaximaTime: lastMaximaTime
  });
  
  return tails;
};

// Create tail notes - exclude points with unnatural drops
export const createTailNotes = (tail, segmentIndex, allNotes, totalSegmentDuration, currentTime, globalMaxIntensity, pitchDerivative) => {
  const tailNotes = [];
  
  // Get all key points: inflection points + last point
  const keyPoints = [...tail.inflectionPoints.map(ip => ({
    point: ip.point,
    type: `tail-${ip.type}`,
    index: ip.index
  }))];
  
  // Add last point (only if natural)
  if (tail.tailPoints.length > 0) {
    const lastPoint = tail.tailPoints[tail.tailPoints.length - 1];
    const lastIntensity = findIntensityAtTime(tail.segment.intensityPoints, lastPoint.time);
    
    // Check if last point has natural pitch behavior
    const isLastPointNatural = isNaturalPitchPoint(lastPoint, tail.tailPoints, globalMaxIntensity);
    
    if (lastPoint.pitch > 50 && lastIntensity > (globalMaxIntensity * 0.1) && isLastPointNatural) {
      keyPoints.push({
        point: lastPoint,
        type: 'tail-end',
        index: tail.tailPoints.length - 1
      });
    }
  }
  
  // Sort key points by time
  keyPoints.sort((a, b) => a.point.time - b.point.time);
  
  if (keyPoints.length === 0) {
    console.log('No natural tail points after filtering steep pitch drops');
    return;
  }
  
  // Calculate durations between key points
  const keyPointsWithDurations = calculateTailDurations(keyPoints, tail.tailPoints, tail.lastMaximaTime);
  
  // Create notes for each key point with calculated durations
  keyPointsWithDurations.forEach((keyPoint, i) => {
    const intensity = findIntensityAtTime(tail.segment.intensityPoints, keyPoint.point.time);
    
    // Check if point has natural pitch behavior
    const isNatural = isNaturalPitchPoint(keyPoint.point, tail.tailPoints, globalMaxIntensity);
    
    if (keyPoint.point.pitch > 50 && intensity > (globalMaxIntensity * 0.1) && isNatural) {
      const tailNote = createTailPointNote(
        keyPoint.point, 
        keyPoint.type, 
        intensity, 
        keyPoint.duration,
        tail, 
        segmentIndex, 
        totalSegmentDuration, 
        currentTime, 
        globalMaxIntensity
      );
      tailNotes.push(tailNote);
    }
  });
  
  tailNotes.forEach(note => allNotes.push(note));
  
  console.log(`Created ${tailNotes.length} natural tail notes after filtering steep drops`);
};

// Helper function to check if a point has natural pitch behavior
const isNaturalPitchPoint = (point, tailPoints, globalMaxIntensity) => {
  const pointIndex = tailPoints.findIndex(p => p.time === point.time);
  if (pointIndex <= 0) return true; // First point is always natural
  
  const prevPoint = tailPoints[pointIndex - 1];
  const timeDiff = point.time - prevPoint.time;
  const pitchDiff = point.pitch - prevPoint.pitch;
  
  if (timeDiff <= 0) return true;
  
  const pitchChangeRate = pitchDiff / timeDiff;
  
  // Exclude if pitch drops too sharply (more than 200 Hz/second)
  const isNatural = pitchChangeRate > -200;
  
  if (!isNatural) {
    console.log(`Excluded unnatural point at ${point.time.toFixed(2)}s: pitch drop rate ${pitchChangeRate.toFixed(0)} Hz/s`);
  }
  
  return isNatural;
};